import {useCallback, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {fetchNotifications} from "./notification-api";
import {useNotifications} from "./notification-context";
import coalescedRequest from "./coalesced-request";

export default function useNotificationFeed({enabled = true, size = 10}) {
    const center = useNotifications(), {i18n} = useTranslation();
    const [filter, setFilter] = useState("all"), [search, setSearch] = useState(""), [term, setTerm] = useState("");
    const [page, setPage] = useState(0), [result, setResult] = useState(null);
    const request = useRef(null), revision = useRef(center.revision);
    const scope = `${center.scope}:${i18n.language}:${filter}:${term}:${page}:${size}`;
    useEffect(() => {
        if (search.trim() === term) return;
        const timer = setTimeout(() => {setTerm(search.trim()); setPage(0);}, 300);
        return () => clearTimeout(timer);
    }, [search, term]);
    useEffect(() => {
        if (!enabled || !center.enabled) return;
        const work = coalescedRequest(async signal => {
            // Polling must not reinsert a spinner or hide a previously loaded empty state.
            setResult(previous => previous?.scope === scope && previous.data ? previous
                : {scope, data: null, loading: true, error: ""});
            await fetchNotifications({page, size, read: filter === "unread" ? false : undefined, search: term}, i18n.language, signal)
            .then(data => {
                if (signal.aborted) return;
                // The tenant-wide feed must never render a courier's personal notification.
                const valid = data.content.every(row => !row.recipientUserId && row.tenantId === center.tenantId);
                if (!valid) throw new Error("Unexpected notification scope");
                center.observe(data.content);
                if (page > 0 && page >= data.totalPages) {setPage(Math.max(0, data.totalPages - 1)); return;}
                setResult(previous => previous?.scope === scope && !previous.loading && !previous.error
                    && JSON.stringify(previous.data) === JSON.stringify(data) ? previous : {scope, data, loading: false, error: ""});
            }).catch(error => {
                if (!signal.aborted) setResult(previous => ({scope, data: previous?.scope === scope ? previous.data : null,
                    loading: false, error: error.response?.data?.message || "FailedToFetchNotifications"}));
            });
        });
        request.current = work;
        work.run();
        return () => {work.dispose(); request.current = null;};
    }, [enabled, center.enabled, center.scope, center.tenantId, center.observe, scope, i18n.language, filter, term, page, size]);
    useEffect(() => {
        if (revision.current === center.revision) return;
        revision.current = center.revision;
        request.current?.run();
    }, [center.revision]);
    const current = result?.scope === scope ? result : {data: null, loading: true, error: ""};
    const changeFilter = useCallback(value => {setFilter(value); setPage(0);}, []);
    return {...current, filter, changeFilter, search, setSearch, page, setPage, refresh: () => request.current?.run()};
}
